import { gql, useMutation, useQuery, useApolloClient } from "@apollo/client";
import React, { useState, useContext, useEffect } from "react";
import {
  LoadingView,
  InvalidReasons,
  NotificationContext,
} from "@solsys/solsys-reactjs";
import SaveButton from "../elements/SaveButton";
import {
  useCreateIdempotencyKeys,
  fingerprintProperties,
  mutationInput,
} from "../../utils/idempotency";
import ProspectDropDown from "../../dropdowns/ProspectDropDown";
import CraneDropDown from "../../dropdowns/CraneDropDown";

const entityQuery = gql`
  query tagList($tag_list_request: tag_list_request,
  $tag2Crane_list_request: tag2Crane_list_request,
  $tag2Prospect_list_request: tag2Prospect_list_request) {
    tag(tag_list_request: $tag_list_request) {
      edges {
        id
        name
      }
      totalCount
    }
    tag2Crane(tag2Crane_list_request: $tag2Crane_list_request) {
      edges {
        id
        crane_id
      }
      totalCount
    }
    tag2Prospect(tag2Prospect_list_request: $tag2Prospect_list_request) {
      edges {
        id
        prospect_id
      }
      totalCount
    }
  }
`;

const entityMutation = gql`
  mutation tagMutate($tag_mutation_request: MutationInput) {
    tag(tag_mutation_request: $tag_mutation_request) {
      id
      isNew
    }
  }
`;

const tag2CraneMutation = gql`
  mutation tag2CraneMutate($tag2Crane_mutation_request: MutationInput) {
    tag2Crane(tag2Crane_mutation_request: $tag2Crane_mutation_request) {
      id
      isNew
    }
  }
`;

const tag2ProspectMutation = gql`
  mutation tag2ProspectMutate($tag2Prospect_mutation_request: MutationInput) {
    tag2Prospect(tag2Prospect_mutation_request: $tag2Prospect_mutation_request) {
      id
      isNew
    }
  }
`;

export default function TagEdit({ id: entityId, action, navigate }) {
  
  const client = useApolloClient();
  const isEdit = action === "edit";
  const { addNotification } = useContext(NotificationContext);
  // variables for entity query const
  let variables = {
    tag_list_request: {
      filters: [
        {
          field: "id",
          operator: "=",
          value: String(entityId ?? 0),
        },
      ],
    },
    tag2Crane_list_request: {
      filters: [{
        field: "tag_id",
        operator: "=",
        value: String(entityId ?? 0),
      }]
    },
    tag2Prospect_list_request: {
      filters: [{
        field: "tag_id",
        operator: "=",
        value: String(entityId ?? 0),
      }]
    }
  };

  const [name, setName] = useState(null);
  const [craneIds, setCraneIds] = useState([]);
  const [prospectIds, setProspectIds] = useState([]);
  const [craneList, setCranelist] = useState([]);
  const [prospectList, setProspectList] = useState([]);

  const [invalidReasons, setInvalidReasons] = useState([]);
  const createKeys = useCreateIdempotencyKeys();
  const tagProperties = [{ key: "name", string: name }];

  // fetches entity and sets up refetch
  const {
    data,
    loading: loadingEntity,
    refetch: refetchEntity,
  } = useQuery(entityQuery, {
    variables,
    fetchPolicy: "network-only",
    onCompleted: (localData) => {
      const listResponse = localData?.tag;
      const cranes = localData?.tag2Crane?.edges ?? [];
      const prospects = localData?.tag2Prospect?.edges ?? [];
      const list = listResponse?.edges ?? [];
      const tag = list[0];
      setName(tag?.name);
      setCraneIds(cranes.map(crane => crane.crane_id));
      setProspectIds(prospects.map(prospect => prospect.prospect_id));
      setCranelist(cranes);
      setProspectList(prospects);
    },
    onError: (error) => {
      console.error(error);
    },
  });
  const tag = data?.tag?.edges?.[0];

  // sets up Tag mutation
  const [mutateFunction, mutateInfo] = useMutation(entityMutation, {
    variables: {
      tag_mutation_request: mutationInput({
        id: isEdit ? entityId : undefined,
        properties: tagProperties,
        idempotencyKey: isEdit
          ? undefined
          : createKeys.keyFor("tag", fingerprintProperties(tagProperties)),
      }),
    },
    onError: (error) => {
      console.error(error);
      addNotification("bad", "Something went wrong");
    },
    onCompleted: (response, originalRequestVariables) => {
      let id = response?.tag?.id;
      mutateProject(id);
      navigate({
        action: "edit",
        id,
        push: false,
      });
      refetchEntity();
    },
  });
  
  useEffect(() => {
    refetchEntity();
  }, [entityId])

  const validateForm = () => {
    const reasons = [];
    if (!name || name.trim() === "") reasons.push("Name is required.");

    setInvalidReasons(reasons);
    return reasons.length === 0;
  };

  const mutateProject = async (tag_id) => {
    const tag2CraneMutateList = [];
    const tag2ProspectMutateList = [];
    craneList.forEach((crane) => {
      if (!craneIds.includes(crane.crane_id)) {
        tag2CraneMutateList.push({ id: crane.id, tag_id: tag_id, crane_id: crane.crane_id });
      }
    });
    craneIds.forEach((id) => {
      if (!craneList.some((crane) => crane.crane_id == id)) {
        tag2CraneMutateList.push({ id: 0, tag_id: tag_id, crane_id: id });
      }
    });
    prospectList.forEach((prospect) => {
      if (!prospectIds.includes(prospect.prospect_id)) {
        tag2ProspectMutateList.push({ id: prospect.id, tag_id: tag_id, prospect_id: prospect.prospect_id });
      }
    });
    prospectIds.forEach((id) => {
      if (!prospectList.some((prospect) => prospect.prospect_id == id)) {
        tag2ProspectMutateList.push({ id: 0, tag_id: tag_id, prospect_id: id });
      }
    });
    const tag2CraneMutationPromises = tag2CraneMutateList.map((crane) => {
      client.mutate({
        mutation: tag2CraneMutation,
        variables: {
          tag2Crane_mutation_request: mutationInput({
            id: crane.id > 0 ? crane.id : undefined,
            shouldDelete: crane.id > 0,
            properties: [
              { key: "tag_id", number: Number(crane.tag_id) },
              { key: "crane_id", number: Number(crane.crane_id) },
            ],
            idempotencyKey:
              crane.id > 0
                ? undefined
                : createKeys.keyFor(`crane:${crane.crane_id}`, `${tag_id}:${crane.crane_id}`),
          }),
        },
      });
    })
    const tag2ProspectMutationPromises = tag2ProspectMutateList.map((prospect) => {
      client.mutate({
        mutation: tag2ProspectMutation,
        variables: {
          tag2Prospect_mutation_request: mutationInput({
            id: prospect.id > 0 ? prospect.id : undefined,
            shouldDelete: prospect.id > 0,
            properties: [
              { key: "tag_id", number: Number(prospect.tag_id) },
              { key: "prospect_id", number: Number(prospect.prospect_id) },
            ],
            idempotencyKey:
              prospect.id > 0
                ? undefined
                : createKeys.keyFor(`prospect:${prospect.prospect_id}`, `${tag_id}:${prospect.prospect_id}`),
          }),
        },
      });
    })
    const allMutationPromises = [...tag2CraneMutationPromises, ...tag2ProspectMutationPromises,];
  
    try {
      // Wait for all mutations to complete
      const results = await Promise.all(allMutationPromises);
      addNotification("good", "Tag saved successfully");

    } catch (error) {
      // Handle errors
      console.error("One or more mutations failed:", error);
      addNotification("bad", "Something went wrong");
    }
  }
  let loadingMutation = mutateInfo.loading;

  return loadingEntity ? (
    <LoadingView />
  ) : (
    <>
      <div className="page-header">
        <span className="page-header-title">
          {isEdit ? "Edit" : "Add"} Tag Detail
        </span>
      </div>

      <div className="add-edit">
        <div className="edit-section">
          <div className="edit-property">
            <label>Name *:</label>
            <input value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="edit-property">
            <label>Crane:</label>
            <CraneDropDown
              selected={craneIds}
              onSelect={setCraneIds}
              searchEnabled
              multiple={true}
            />
          </div>
          <div className="edit-property">
            <label>Prospect:</label>
            <ProspectDropDown
              selected={prospectIds}
              onSelect={setProspectIds}
              searchEnabled
              multiple={true}
            />
          </div>
        </div>
        {invalidReasons.length > 0 && (
          <InvalidReasons reasons={invalidReasons} />
        )}
        <div className="edit-section">
          <SaveButton
            saving={!isEdit && createKeys.isSaving}
            disabled={isEdit && loadingMutation}
            onClick={() => {
              if (!validateForm()) return;
              if (isEdit) mutateFunction();
              else createKeys.trackSave(() => mutateFunction());
            }}
          />
        </div>
      </div>
    </>
  );
}
